import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select, Spin } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { getAllInternalUsersAction, getAllOrdersAction, getAllSubscriptionAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { Status } from '@app/components/profile/profileCard/profileFormNav/nav/payments/paymentHistory/Status/Status';
const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const [filters, setFilters] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: ''
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  let navigate = useNavigate();
  // Fetch data with updated filters and pagination
  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAllInternalUsersAction(params)).unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, dispatch]);



  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      render: (text, record) => <span>{record?.firstName || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search First Name"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('firstName', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      // onFilter: (value, record) => record?.firstName.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      render: (text, record) => <span>{record?.lastName || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Last Name"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('lastName', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => record?.lastName.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (text, record) => <span>{record?.email || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Email"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('email', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => record?.email.includes(value),
    },

    // {
    //   title: 'Delivery Date',
    //   dataIndex: 'delivery_date',
    //   render: (text, record) =>
    //     record?.delivery_dates?.[0]?.date
    //       ? moment(record.delivery_dates[0].date).format('DD-MMM-YYYY')
    //       : 'Not available',
    //   filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    //     <div style={{ padding: 8 }}>
    //       <RangePicker
    //         value={selectedKeys[0] ? selectedKeys : []}
    //         onChange={(dates) => {
    //           setSelectedKeys(dates || []);
    //           handleFilterChange('deliveryDateRange', dates || []); // Update the filter state
    //         }}
    //         style={{ width: 188, marginBottom: 8, display: 'block' }}
    //       />
    //       <Button
    //         type="primary"
    //         onClick={() => confirm()}
    //         size="small"
    //         style={{ width: 90, marginRight: 8 }}
    //       >
    //         Search
    //       </Button>
    //       <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
    //         Reset
    //       </Button>
    //     </div>
    //   ),
    // },
    {
      title: 'Role',
      dataIndex: 'role',
      render: (text, record) => (
        <Badge
          status={record?.role?.toLowerCase()}
          text={record?.role || 'Not available'}
        />
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || ''}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('role', value); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8 }}
            placeholder="Select Status"
          >
            <Option value="Admin">Admin</Option>
            <Option value="Analyst">Analyst</Option>

          </Select>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => record?.role.toLowerCase() === value.toLowerCase(),
    },
    {
      title: t('common.status'),
      key: 'isActive',
      dataIndex: 'isActive',
      render: (isActive: Boolean) => (
        <BaseRow gutter={[10, 10]}>


          <BaseCol>
            <Status color={isActive ? 'var(--success-color)' : 'var(--error-color)'} text={isActive ? 'ACTIVE' : 'INACTIVE'} />
          </BaseCol>


        </BaseRow>
      ),
    },
    {
      title: "Actions",
      dataIndex: 'actions',
      width: '15%',
      render: (text: string, record: any) => {
        return (
          <BaseSpace>
            <BaseButton
              type="ghost"
              onClick={() => {
                navigate('/admin/users/edit/' + record?._id)
                // notificationController.info({ message: t('tables.inviteMessage', { name: record.name }) });
              }}
            >
              {t('common.edit')}
            </BaseButton>
          </BaseSpace>
        );
      },
    },
  ];

  return (
    <>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          setFilters({
            firstName: '',
            lastName: '',
            email: '',
            role: ''
          });

          setTimeout(() => {
            setPagination({ current: 1, pageSize: 20 });

          }, 1000);
        }}>Clear Filters</Button>
      {!tableData?.loading && <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
      }
      <div className='d-block'>
        {tableData?.loading && <Spin spinning={tableData?.loading} />}
      </div>
    </>
  );
};
